import type { Message } from "@/dtos/Unipile.dto";

export type MessageStatus = "sending" | "sent" | "delivered" | "read" | "failed";

export interface MessageStatusInfo {
  status: MessageStatus;
  icon: string;
  color: string;
  label: string;
}

/**
 * Get message status for WhatsApp-style indicators
 */
export function getWhatsAppMessageStatus(message: Message): MessageStatusInfo {
  if (!message.isOwn) {
    return {
      status: "sent",
      icon: "",
      color: "",
      label: "",
    };
  }

  // Check if message has been read
  const hasReadReceipts = message.readReceipts && message.readReceipts.length > 0;
  const readCount = message.readCount || 0;
  const seenBy = message.seenBy || {};

  // If there are read receipts or seen_by entries, message is read
  if (hasReadReceipts || readCount > 0 || Object.keys(seenBy).length > 0) {
    return {
      status: "read",
      icon: "mdi:check-all",
      color: "#34b7f1",
      label: "Read",
    };
  }

  // Check delivered field first
  if (message.delivered === true) {
    return {
      status: "delivered",
      icon: "mdi:check-all",
      color: "#8696a0",
      label: "Delivered",
    };
  }

  // If delivered is explicitly false, show sent
  if (message.delivered === false) {
    return {
      status: "sent",
      icon: "mdi:check",
      color: "#8696a0", // Gray
      label: "Sent",
    };
  }

  // Fallback: assume delivered if message has a timestamp and is not new
  const isDelivered = message.timestamp && new Date(message.timestamp) < new Date();

  if (isDelivered) {
    return {
      status: "delivered",
      icon: "mdi:check-all",
      color: "#8696a0", // Gray
      label: "Delivered",
    };
  }

  // Default to sent
  return {
    status: "sent",
    icon: "mdi:check",
    color: "#8696a0", // Gray
    label: "Sent",
  };
}

/**
 * Get message status for LinkedIn-style indicators
 */
export function getLinkedInMessageStatus(message: Message): MessageStatusInfo {
  // If message is not from current user, no status indicator
  if (!message.isOwn) {
    return {
      status: "sent",
      icon: "",
      color: "",
      label: "",
    };
  }

  // Check if message has been read
  const hasReadReceipts = message.readReceipts && message.readReceipts.length > 0;
  const readCount = message.readCount || 0;
  const seenBy = message.seenBy || {};

  // If there are read receipts or seen_by entries, message is read
  if (hasReadReceipts || readCount > 0 || Object.keys(seenBy).length > 0) {
    return {
      status: "read",
      icon: "mdi:linkedin",
      color: "#0077b5",
      label: "Read",
    };
  }

  // Check delivered field first (more reliable than timestamp)
  if (message.delivered === true) {
    return {
      status: "delivered",
      icon: "mdi:check-all",
      color: "#8696a0",
      label: "Delivered",
    };
  }

  // If delivered is explicitly false, show sent
  if (message.delivered === false) {
    return {
      status: "sent",
      icon: "mdi:check",
      color: "#8696a0",
      label: "Sent",
    };
  }

  // Default to sent (single checkmark)
  return {
    status: "sent",
    icon: "mdi:check",
    color: "#8696a0",
    label: "Sent",
  };
}

/**
 * Get message status for any platform
 */
export function getMessageStatus(
  message: Message,
  platform: "whatsapp" | "linkedin"
): MessageStatusInfo {
  switch (platform) {
    case "whatsapp":
      return getWhatsAppMessageStatus(message);
    case "linkedin":
      return getLinkedInMessageStatus(message);
    default:
      // Default to WhatsApp style for unknown platforms
      return getWhatsAppMessageStatus(message);
  }
}

/**
 * Format timestamp for message display
 */
export function formatMessageTimestamp(timestamp: string | Date): string {
  const date = typeof timestamp === "string" ? new Date(timestamp) : timestamp;
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  // Just now
  if (diffMins < 1) {
    return "Just now";
  }

  // Minutes ago
  if (diffMins < 60) {
    return `${diffMins}m`;
  }

  // Hours ago
  if (diffHours < 24) {
    return `${diffHours}h`;
  }

  // Days ago
  if (diffDays < 7) {
    return `${diffDays}d`;
  }

  // Older messages - show date
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
  });
}

/**
 * Format full timestamp for message display
 */
export function formatFullMessageTimestamp(timestamp: string | Date): string {
  const date = typeof timestamp === "string" ? new Date(timestamp) : timestamp;
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / 86400000);

  // Today - show time only
  if (diffDays === 0) {
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  }

  // Yesterday
  if (diffDays === 1) {
    return `Yesterday ${date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })}`;
  }

  // This week - show day and time
  if (diffDays < 7) {
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  }

  // Older - show full date
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}
